import { cn } from "app/lib/utils"
import { Slider } from "radix-ui"
import { ComponentProps } from "react"
import type { PlayerVolumeColor } from "app/player/getVolumeIcon"

type Props = {
  volume: number
  onVolumeValueChange: (volume: number) => void
  color: PlayerVolumeColor
  orientation?: "horizontal" | "vertical"
  className?: string
} & Omit<
  ComponentProps<typeof Slider.Root>,
  | "value"
  | "onValueChange"
  | "min"
  | "max"
  | "step"
  | "orientation"
  | "onChange"
  | "onVolumeChange"
>

const trackColorClasses: Record<PlayerVolumeColor, string> = {
  blue: "bg-neutraal-700",
  gray: "bg-neutraal-200",
  white: "bg-neutraal-200",
}

const rangeColorClasses: Record<PlayerVolumeColor, string> = {
  blue: "bg-white",
  gray: "bg-neutraal-900",
  white: "bg-neutraal-900",
}

export default function PlayerVolumeTrack({
  volume,
  onVolumeValueChange,
  color,
  orientation = "horizontal",
  className,
  ...props
}: Props) {
  const isVertical = orientation === "vertical"

  return (
    <Slider.Root
      className={cn(
        "relative flex touch-none items-center select-none mr-[1px]",
        isVertical
          ? "h-[100px] w-[46px] flex-col justify-center"
          : "w-[100px] items-center justify-center py-3",
        className
      )}
      orientation={orientation}
      min={0}
      max={1}
      step={0.01}
      value={[volume]}
      onValueChange={(values) => onVolumeValueChange(values[0] ?? 0)}
      aria-label="Volume"
      {...props}
    >
      <Slider.Track
        className={cn(
          "relative grow rounded-full border-[1px] border-black",
          isVertical ? "h-full w-2.5" : "h-2.5 w-full",
          trackColorClasses[color]
        )}
      >
        <Slider.Range
          className={cn(
            "absolute rounded-full",
            isVertical ? "w-full" : "h-full",
            rangeColorClasses[color]
          )}
        />
      </Slider.Track>
      <Slider.Thumb className="block size-[26px] rounded-full border border-black bg-gray-25 shadow-[0_0_8px_rgba(0,0,0,0.25)] focus:outline-none" />
    </Slider.Root>
  )
}
